<script setup lang="ts">
import { computed, ref, useTemplateRef } from 'vue';
import { computedAsync, useElementSize } from '@vueuse/core';

import {
  useCoreStore,
  useRouterStore,
  useStatsigEventsStore,
} from '@apps/main/new-desktop/core/stores';
import type {
  AccommodationProductCardData,
  AlternativeHotel,
  AvailableItineraryAccommodationSegment,
} from '@apps/main/new-desktop/pages/tripOverview/models';

import {
  AppArrowScroller,
  AppButton,
  AppAutoplayGallery,
  AppLazyLoadComponent,
  AppRawButton,
} from '@libs/ui';
import { isMultiExperimentAsync } from '@libs/feature-toggle';

import DateCardTag from '../DateCardTag.vue';
import TripOverviewTrashIconButton from '../../TripOverviewTrashIconButton.vue';

import DesktopProductCardLayout from './DesktopProductCardLayout.vue';
import AccommodationProductCardContent from './AccommodationProductCardContent.vue';
import AlternativeAccommodationCardContent from './AlternativeAccommodationCardContent.vue';
import AccommodationPrice from './AccommodationPrice.vue';
import AccommodationCardSelectedFooter from './AccommodationCardSelectedFooter.vue';
import GenericHotelCard from './GenericHotelCard.vue';

interface AccommodationCarouselDesktopProps {
  segment: AvailableItineraryAccommodationSegment;
  printMode: boolean;
  shouldHideProductLink: boolean;
}

const props = defineProps<AccommodationCarouselDesktopProps>();

interface AccommodationCarouselDesktopEmits {
  (event: 'open-hotel-selection'): void;
  (event: 'remove-accommodation'): void;
}

const emit = defineEmits<AccommodationCarouselDesktopEmits>();

const coreStore = useCoreStore();
const statsigEventsStore = useStatsigEventsStore();
const routerStore = useRouterStore();

const activeAutoplayGalleryIndex = ref<number | null>(null);
const firstCardRef = useTemplateRef('firstCardRef');
const firstCardSize = useElementSize(firstCardRef);

const isOverviewShorterHotelsCarouselExperiment = computedAsync<
  string | boolean
>(() => isMultiExperimentAsync('overview_shorter_hotels_carousel'), 'control');

const alternativeHotels = computed<AlternativeHotel[]>(() => {
  if (isOverviewShorterHotelsCarouselExperiment.value === 'none') {
    return [];
  }

  if (isOverviewShorterHotelsCarouselExperiment.value === 'partial') {
    const partialAlternativeHotelNumbers = 3;
    return props.segment.alternativeHotels.slice(
      0,
      partialAlternativeHotelNumbers
    );
  }

  return props.segment.alternativeHotels;
});

const hotelCards = computed(() => {
  if (props.printMode || props.shouldHideProductLink) {
    return [props.segment.productCardData];
  }
  return [props.segment.productCardData, ...alternativeHotels.value, null];
});

function showDetails(
  hotel: AlternativeHotel | AccommodationProductCardData,
  context: {
    isAlternative: boolean;
    shouldScrollToRooms: boolean;
    position?: number;
    clickSource:
      | 'detailsButtonExperiment'
      | 'mainCard'
      | 'suggestionCard'
      | 'changeAccommodationButtonOnSuggestionCard'
      | undefined;
  }
): void {
  if (props.printMode) {
    return;
  }

  if (context.clickSource) {
    statsigEventsStore.logExperimentAccommodationActionClicked(
      context.clickSource
    );
  }

  const hotelCode = isSelectedHotel(hotel)
    ? props.segment.context.hotelCode
    : hotel.hotelCode;

  if (context.isAlternative && context.position) {
    statsigEventsStore.isAlternativeHotelOpened = true;
    statsigEventsStore.logTripOverviewAlternativeHotelsCardClicked({
      position: context.position,
      hotelCode: hotelCode,
    });
  }

  routerStore.navigateToHotelDetailsInTripOverview(
    {
      ingredientIndex: props.segment.context.ingredientIndex,
    },
    hotelCode,
    context.shouldScrollToRooms
  );
}

function openHotelSelection(): void {
  statsigEventsStore.logExperimentAccommodationActionClicked(
    'changeButtonExperiment'
  );
  emit('open-hotel-selection');
}

function removeAccommodation(): void {
  statsigEventsStore.logExperimentIngredientRemoved('accommodation');
  emit('remove-accommodation');
}

function isSelectedHotel(
  hotel: AlternativeHotel | AccommodationProductCardData
): hotel is AccommodationProductCardData {
  return 'productCardContent' in hotel;
}

function showDetailsExperiment(
  accommodationItem: AlternativeHotel | AccommodationProductCardData,
  clickSource: 'mainCard' | undefined
): void {
  if (props.shouldHideProductLink) {
    return;
  }

  showDetails(accommodationItem, {
    isAlternative: false,
    shouldScrollToRooms: false,
    clickSource,
  });
}
</script>

<template>
  <div
    :class="[
      'accommodation-products-carousel-desktop',
      { 'print-mode': props.printMode },
    ]"
  >
    <AppArrowScroller
      :items="hotelCards"
      :item-key-selector="(_, index) => index"
      :arrow-vertical-position="firstCardSize.height.value / 2"
      :display-dashed-border="false"
      :print-mode="props.printMode"
      :show-arrows="hotelCards.length > 1"
      :left-arrow-aria-label="$t('a11y.left_arrow_previous_suggestions')"
      :right-arrow-aria-label="$t('a11y.right_arrow_next_suggestions')"
    >
      <template #item="{ item: accommodationItem, index }">
        <div
          v-if="accommodationItem && isSelectedHotel(accommodationItem)"
          :class="['card', { single: props.shouldHideProductLink }]"
        >
          <DesktopProductCardLayout
            ref="firstCardRef"
            :print-mode="props.printMode"
            :should-hide-product-link="props.shouldHideProductLink"
            @mouseenter="activeAutoplayGalleryIndex = index"
            @mouseleave="activeAutoplayGalleryIndex = null"
            @focusin="activeAutoplayGalleryIndex = index"
            @focusout="activeAutoplayGalleryIndex = null"
          >
            <template #left-section>
              <AppLazyLoadComponent>
                <AppAutoplayGallery
                  :image-urls="accommodationItem.imgSrcs"
                  :is-active="
                    !props.printMode && activeAutoplayGalleryIndex === index
                  "
                  :error-image-handler="
                    (src) =>
                      coreStore.handleImageLoadError({
                        src: src,
                        source:
                          'accommodation-products-carousel-desktop-first-card-autoplay-gallery',
                      })
                  "
                  @click="showDetailsExperiment(accommodationItem, 'mainCard')"
                />
              </AppLazyLoadComponent>
            </template>
            <template #content>
              <AccommodationProductCardContent
                :product-card-content="accommodationItem.productCardContent"
                :context="props.segment.context"
                :print-mode="props.printMode"
                :should-hide-product-link="props.shouldHideProductLink"
                @show-details="
                  showDetailsExperiment(accommodationItem, 'mainCard')
                "
                @show-rooms="
                  showDetails(accommodationItem, {
                    isAlternative: false,
                    shouldScrollToRooms: true,
                    clickSource: undefined,
                  })
                "
              />
            </template>
            <template #footer>
              <AccommodationCardSelectedFooter
                v-if="!props.printMode && !props.shouldHideProductLink"
                :ingredient-index="props.segment.context.ingredientIndex"
              />
              <div
                v-if="!props.printMode && props.shouldHideProductLink"
                class="button-wrapper"
              >
                <AppRawButton
                  class="details"
                  @click="
                    showDetails(accommodationItem, {
                      isAlternative: false,
                      shouldScrollToRooms: false,
                      clickSource: 'detailsButtonExperiment',
                    })
                  "
                >
                  {{ $t('planner.details') }}
                </AppRawButton>
                <AppButton
                  state="enabled"
                  type="primary"
                  size="small"
                  :ariaLabel="$t('a11y.change_accommodation')"
                  @click="openHotelSelection"
                >
                  {{ $t('buttons.stay') }}
                </AppButton>
              </div>
            </template>
            <template #left-section-tag>
              <DateCardTag :date="props.segment.productCardData.date" />
            </template>
          </DesktopProductCardLayout>

          <TripOverviewTrashIconButton
            v-if="props.shouldHideProductLink && !props.printMode"
            class="trash-icon"
            :with-border="false"
            :rounded="false"
            :ariaLabel="$t('a11y.remove_stay')"
            @remove="removeAccommodation"
          />
        </div>
        <div
          v-if="
            accommodationItem &&
            !isSelectedHotel(accommodationItem) &&
            !props.printMode
          "
          class="card"
        >
          <DesktopProductCardLayout
            :should-hide-product-link="props.shouldHideProductLink"
            @mouseenter="activeAutoplayGalleryIndex = index"
            @mouseleave="activeAutoplayGalleryIndex = null"
            @focusin="activeAutoplayGalleryIndex = index"
            @focusout="activeAutoplayGalleryIndex = null"
          >
            <template #left-section>
              <AppLazyLoadComponent>
                <AppAutoplayGallery
                  :image-urls="accommodationItem.imgUrls"
                  :is-active="
                    !props.printMode && activeAutoplayGalleryIndex === index
                  "
                  :error-image-handler="
                    (src) =>
                      coreStore.handleImageLoadError({
                        src: src,
                        source:
                          'accommodation-products-carousel-desktop-autoplay-gallery',
                      })
                  "
                  @click="
                    showDetails(accommodationItem, {
                      isAlternative: true,
                      shouldScrollToRooms: false,
                      position: index - 1,
                      clickSource: 'suggestionCard',
                    })
                  "
                />
              </AppLazyLoadComponent>
            </template>
            <template #content>
              <AlternativeAccommodationCardContent
                :hotel="accommodationItem"
                :ingredient-index="props.segment.context.ingredientIndex"
                @show-details="
                  showDetails(accommodationItem, {
                    isAlternative: true,
                    shouldScrollToRooms: false,
                    position: index - 1,
                    clickSource: 'suggestionCard',
                  })
                "
                @show-rooms="
                  showDetails(accommodationItem, {
                    isAlternative: true,
                    shouldScrollToRooms: true,
                    position: index - 1,
                    clickSource: undefined,
                  })
                "
              />
            </template>
            <template #footer>
              <div class="footer">
                <AccommodationPrice :price="accommodationItem.deltaPrice" />
                <AppButton
                  type="primary"
                  :left-icon="{ name: 'change' }"
                  state="enabled"
                  size="small"
                  @click="
                    (showDetails(accommodationItem, {
                      isAlternative: true,
                      shouldScrollToRooms: false,
                      clickSource: 'changeAccommodationButtonOnSuggestionCard',
                    }),
                    statsigEventsStore.logTripOverviewChangeAlternateHotelCardClicked())
                  "
                >
                  {{ $t('overview_stay_footer.change') }}
                </AppButton>
              </div>
            </template>
            <template #left-section-tag>
              <DateCardTag :date="props.segment.productCardData.date" />
            </template>
          </DesktopProductCardLayout>
        </div>

        <div v-if="accommodationItem === null && !props.printMode" class="card">
          <GenericHotelCard
            :ingredient-index="props.segment.context.ingredientIndex"
          />
        </div>
      </template>
    </AppArrowScroller>
    <slot />
  </div>
</template>

<style scoped lang="scss">
.accommodation-products-carousel-desktop {
  --card-width: 54vw;
  --card-max-width: 850px;
  display: flex;
  flex-direction: column;
  gap: var(--spacing-16);
  background-color: var(--color-off-white);

  @include for-less-or-equal-than-1199-px {
    --card-width: calc(90vw - 60px);
  }

  &.print-mode {
    background-color: var(--color-white);
    --card-width: 100%;

    .card {
      cursor: default;
    }
  }

  .card {
    display: grid;
    grid-auto-rows: 1fr;
    width: var(--card-width);
    height: 100%;
    max-width: var(--card-max-width);
    margin-right: var(--spacing-12);
    cursor: pointer;

    .footer {
      display: flex;
      justify-content: flex-end;
      align-items: center;
      margin: var(--spacing-12) 0 0 0;
      flex-wrap: wrap;
    }

    &.single {
      margin-right: 0;
      --card-width: 100%;
      --card-max-width: 100%;

      .trash-icon {
        position: absolute;
        top: var(--spacing-16);
        right: var(--spacing-16);
      }

      .button-wrapper {
        display: flex;
        justify-content: flex-end;
        gap: var(--spacing-16);
        align-items: center;

        .details {
          @include font-label-m-light;
          text-decoration: underline;
          cursor: pointer;
        }
      }
    }

    &.generic {
      position: relative;
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      gap: var(--spacing-24);
      background-color: var(--color-neutral-10);
      width: var(--card-width);
      max-width: var(--card-max-width);
      border-radius: var(--border-radius-8);
      margin-right: 0;
      overflow: hidden;
      height: 100%;

      @include on-hover {
        cursor: default;
      }

      .generic-card-title {
        color: var(--color-soft-black);
        text-align: center;
        @include font-label-m-bold;
      }

      .heart-illustration {
        position: absolute;
        top: 9px;
        left: 40px;
      }
      .star-illustration {
        position: absolute;
        bottom: -18px;
        right: 25px;
      }
    }
  }
}
</style>
